'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '../../lib/supabase/client';
import {
  PhoneOff,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Phone,
  CheckCircle,
  XCircle,
  Wifi,
} from 'lucide-react';

interface VoiceCallScreenProps {
  chatId: string;
  localUserId: string;
  remoteUserId: string;
  remoteUserName?: string;
  onEnd: () => void;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

type CallPhase = 'connecting' | 'ringing' | 'active' | 'ended' | 'error';

export default function VoiceCallScreen({
  chatId,
  localUserId,
  remoteUserId,
  remoteUserName = 'Contact',
  onEnd,
}: VoiceCallScreenProps) {
  const supabase = createClient();

  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const signalChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isCaller = localUserId < remoteUserId;

  const [phase, setPhase] = useState<CallPhase>('connecting');
  const [micOn, setMicOn] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const durationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const sendSignal = useCallback(
    async (signalType: string, payload: object) => {
      if (!sessionIdRef.current) return;
      await supabase.from('call_signals').insert({
        session_id: sessionIdRef.current,
        sender_id: localUserId,
        signal_type: signalType,
        payload,
      });
    },
    [localUserId, supabase]
  );

  const cleanup = useCallback(() => {
    if (durationRef.current) clearInterval(durationRef.current);
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    signalChannelRef.current?.unsubscribe();
    pcRef.current = null;
    localStreamRef.current = null;
    signalChannelRef.current = null;
  }, []);

  const endCall = useCallback(async () => {
    if (sessionIdRef.current) {
      await supabase
        .from('call_sessions')
        .update({ call_status: 'ended', updated_at: new Date().toISOString() })
        .eq('id', sessionIdRef.current);
      await sendSignal('end', {});
    }
    cleanup();
    setPhase('ended');
    setTimeout(onEnd, 1200);
  }, [cleanup, onEnd, sendSignal, supabase]);

  // ─── WebRTC setup ────────────────────────────────────────────────────────────

  const createPeerConnection = useCallback(
    (_sessionId: string) => {
      const pc = new RTCPeerConnection(ICE_SERVERS);
      pcRef.current = pc;

      // Add local audio tracks only
      localStreamRef.current?.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current!);
      });

      // Remote audio stream → audio element
      pc.ontrack = (e) => {
        if (remoteAudioRef.current && e.streams[0]) {
          remoteAudioRef.current.srcObject = e.streams[0];
          setPhase('active');
          durationRef.current = setInterval(
            () => setCallDuration((d) => d + 1),
            1000
          );
        }
      };

      // ICE candidates → signaling channel
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          sendSignal('ice', { candidate: e.candidate.toJSON() });
        }
      };

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === 'disconnected' ||
          pc.connectionState === 'failed'
        ) {
          endCall();
        }
      };

      return pc;
    },
    [endCall, sendSignal]
  );

  // ─── Subscribe to incoming signals ──────────────────────────────────────────

  const subscribeToSignals = useCallback(
    (sessionId: string) => {
      const channel = supabase
        .channel(`voice_signals:${sessionId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'call_signals',
            filter: `session_id=eq.${sessionId}`,
          },
          async (payload) => {
            const signal = payload.new as {
              sender_id: string;
              signal_type: string;
              payload: Record<string, unknown>;
            };

            if (signal.sender_id === localUserId) return;

            const pc = pcRef.current;
            if (!pc) return;

            if (signal.signal_type === 'answer') {
              await pc.setRemoteDescription(
                new RTCSessionDescription(signal.payload as RTCSessionDescriptionInit)
              );
            } else if (signal.signal_type === 'offer') {
              await pc.setRemoteDescription(
                new RTCSessionDescription(signal.payload as RTCSessionDescriptionInit)
              );
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              await sendSignal('answer', answer);
            } else if (signal.signal_type === 'ice') {
              try {
                await pc.addIceCandidate(
                  new RTCIceCandidate((signal.payload as { candidate: RTCIceCandidateInit }).candidate)
                );
              } catch (_) {}
            } else if (signal.signal_type === 'end') {
              cleanup();
              setPhase('ended');
              setTimeout(onEnd, 1200);
            }
          }
        )
        .subscribe();

      signalChannelRef.current = channel;
    },
    [cleanup, localUserId, onEnd, sendSignal, supabase]
  );

  // ─── Initiate call (caller) ──────────────────────────────────────────────────

  const startCall = useCallback(async () => {
    try {
      // Audio only — no video
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      localStreamRef.current = stream;

      const { data: session, error } = await supabase
        .from('call_sessions')
        .insert({
          caller_id: localUserId,
          callee_id: remoteUserId,
          chat_id: chatId,
          call_status: 'pending',
        })
        .select()
        .single();

      if (error || !session) throw new Error('Failed to create call session');
      sessionIdRef.current = session.id;

      subscribeToSignals(session.id);
      const pc = createPeerConnection(session.id);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendSignal('offer', offer);

      setPhase('ringing');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not access microphone';
      setErrorMsg(msg);
      setPhase('error');
    }
  }, [chatId, createPeerConnection, localUserId, remoteUserId, sendSignal, subscribeToSignals, supabase]);

  // ─── Receive call (callee) ───────────────────────────────────────────────────

  const receiveCall = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      localStreamRef.current = stream;

      const { data: session } = await supabase
        .from('call_sessions')
        .select('*')
        .eq('chat_id', chatId)
        .eq('callee_id', localUserId)
        .eq('call_status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (session) {
        sessionIdRef.current = session.id;
        await supabase
          .from('call_sessions')
          .update({ call_status: 'active', updated_at: new Date().toISOString() })
          .eq('id', session.id);

        subscribeToSignals(session.id);
        createPeerConnection(session.id);
        setPhase('ringing');
      } else {
        const waitChannel = supabase
          .channel(`voice_wait:${chatId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'call_sessions',
              filter: `chat_id=eq.${chatId}`,
            },
            async (payload) => {
              const newSession = payload.new as Record<string, unknown>;
              if (newSession.callee_id !== localUserId) return;
              sessionIdRef.current = newSession.id as string;
              await supabase
                .from('call_sessions')
                .update({ call_status: 'active', updated_at: new Date().toISOString() })
                .eq('id', newSession.id as string);
              subscribeToSignals(newSession.id as string);
              createPeerConnection(newSession.id as string);
              setPhase('ringing');
              waitChannel.unsubscribe();
            }
          )
          .subscribe();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not access microphone';
      setErrorMsg(msg);
      setPhase('error');
    }
  }, [chatId, createPeerConnection, localUserId, subscribeToSignals, supabase]);

  // ─── Boot ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isCaller) {
      startCall();
    } else {
      receiveCall();
    }
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Controls ────────────────────────────────────────────────────────────────

  const toggleMic = () => {
    localStreamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = !t.enabled;
    });
    setMicOn((v) => !v);
  };

  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = speakerOn;
    }
    setSpeakerOn((v) => !v);
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const avatarLetter = remoteUserName.charAt(0).toUpperCase();

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-between"
      style={{ background: 'linear-gradient(160deg, #0d1b2e 0%, #0a1020 50%, #0d1b2e 100%)' }}
    >
      {/* Hidden audio element for remote stream */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      {/* Top bar */}
      <div className="w-full flex items-center justify-between px-5 pt-12 pb-2">
        <div className="flex flex-col gap-0.5">
          <p className="text-white font-semibold text-lg leading-tight">{remoteUserName}</p>
          {phase === 'active' && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <p className="text-green-400 text-sm font-medium">{formatDuration(callDuration)}</p>
            </div>
          )}
          {phase === 'ringing' && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping" />
              <p className="text-blue-300 text-sm">{isCaller ? 'Ringing…' : 'Incoming call…'}</p>
            </div>
          )}
          {phase === 'connecting' && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <p className="text-yellow-300 text-sm">Connecting…</p>
            </div>
          )}
        </div>

        {phase === 'active' && (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)' }}
          >
            <CheckCircle size={13} className="text-green-400" />
            <span className="text-green-400 text-xs font-semibold">Connected</span>
          </div>
        )}
      </div>

      {/* Center — avatar + state */}
      <div className="flex flex-col items-center gap-6 flex-1 justify-center">
        {/* Pulse rings for ringing state */}
        <div className="relative flex items-center justify-center">
          {phase === 'ringing' && (
            <>
              <span
                className="absolute w-44 h-44 rounded-full animate-ping opacity-10"
                style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}
              />
              <span
                className="absolute w-36 h-36 rounded-full animate-ping opacity-20"
                style={{
                  background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
                  animationDelay: '0.4s',
                }}
              />
              <span
                className="absolute w-28 h-28 rounded-full animate-ping opacity-30"
                style={{
                  background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
                  animationDelay: '0.8s',
                }}
              />
            </>
          )}
          {phase === 'active' && (
            <>
              <span
                className="absolute w-36 h-36 rounded-full animate-ping opacity-15"
                style={{ background: 'rgba(52,211,153,0.4)', animationDuration: '2s' }}
              />
              <span
                className="absolute w-28 h-28 rounded-full animate-ping opacity-20"
                style={{ background: 'rgba(52,211,153,0.4)', animationDuration: '2s', animationDelay: '0.5s' }}
              />
            </>
          )}

          {/* Avatar circle */}
          <div
            className="w-28 h-28 rounded-full flex items-center justify-center text-white font-bold text-5xl relative z-10 shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}
          >
            {avatarLetter}
          </div>
        </div>

        {/* Name */}
        <p className="text-white text-2xl font-bold tracking-tight">{remoteUserName}</p>

        {/* State badge */}
        {phase === 'connecting' && (
          <StateBadge
            icon={<Wifi size={14} className="animate-pulse" />}
            label="Starting call…"
            color="rgba(255,255,255,0.1)"
            textColor="text-white/60"
            pulse
          />
        )}
        {phase === 'ringing' && (
          <StateBadge
            icon={<Phone size={14} className="animate-bounce" />}
            label={isCaller ? 'Ringing…' : 'Incoming voice call…'}
            color="rgba(40,127,216,0.25)"
            textColor="text-blue-300"
            pulse
          />
        )}
        {phase === 'active' && (
          <StateBadge
            icon={<CheckCircle size={14} />}
            label={`Voice call · ${formatDuration(callDuration)}`}
            color="rgba(52,211,153,0.15)"
            textColor="text-green-400"
          />
        )}
        {phase === 'error' && (
          <StateBadge
            icon={<XCircle size={14} />}
            label={errorMsg || 'Connection error'}
            color="rgba(229,62,62,0.2)"
            textColor="text-red-400"
          />
        )}
        {phase === 'ended' && (
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(229,62,62,0.2)', border: '2px solid rgba(229,62,62,0.4)' }}
            >
              <PhoneOff size={32} className="text-red-400" />
            </div>
            <p className="text-white text-lg font-semibold">Call Ended</p>
            <StateBadge
              icon={<XCircle size={14} />}
              label={`Duration: ${formatDuration(callDuration)}`}
              color="rgba(229,62,62,0.2)"
              textColor="text-red-300"
            />
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div
        className="w-full pb-16 pt-8 flex flex-col items-center gap-6"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)' }}
      >
        {/* Secondary controls row */}
        <div className="flex items-center gap-8">
          <CallControlButton
            icon={speakerOn ? <Volume2 size={22} /> : <VolumeX size={22} />}
            label={speakerOn ? 'Mute speaker' : 'Unmute speaker'}
            active={speakerOn}
            onClick={toggleSpeaker}
          />
          <CallControlButton
            icon={micOn ? <Mic size={22} /> : <MicOff size={22} />}
            label={micOn ? 'Mute mic' : 'Unmute mic'}
            active={micOn}
            onClick={toggleMic}
          />
        </div>

        {/* End call — prominent red button */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={endCall}
            aria-label="End call"
            className="rounded-full flex items-center justify-center transition-all duration-150 active:scale-90 shadow-2xl"
            style={{
              width: 72,
              height: 72,
              background: 'linear-gradient(135deg, #E53E3E 0%, #C53030 100%)',
              boxShadow: '0 0 0 6px rgba(229,62,62,0.2), 0 6px 24px rgba(229,62,62,0.45)',
            }}
          >
            <PhoneOff size={30} className="text-white" />
          </button>
          <span className="text-red-400 text-sm font-semibold tracking-wide">End Call</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StateBadge({
  icon,
  label,
  color,
  textColor,
  pulse = false,
}: {
  icon: React.ReactNode;
  label: string;
  color: string;
  textColor: string;
  pulse?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 rounded-full ${pulse ? 'animate-pulse' : ''}`}
      style={{ background: color, border: `1px solid ${color}` }}
    >
      <span className={textColor}>{icon}</span>
      <span className={`text-sm font-medium ${textColor}`}>{label}</span>
    </div>
  );
}

function CallControlButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
      style={{
        background: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.07)',
        border: active ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <span className={active ? 'text-white' : 'text-white/35'}>{icon}</span>
    </button>
  );
}
